import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../store/useStore';
import { useAuthStore } from '../store/useAuthStore';
import { getCountryFromIP, getCountryFromPostalCode, getShippingCost } from '../services/geolocationService';
import { getCurrencyFromCountry, formatPrice, convertToGEL, fetchExchangeRates } from '../utils/currencyUtils';
import '../styles/Checkout.css';

const API_BASE_URL = process.env.REACT_APP_API_URL;

export default function Checkout() {
  const navigate = useNavigate();
  const cart = useStore((state) => state.cart);
  const products = useStore((state) => state.products);
  const user = useAuthStore((state) => state.user);
  const token = useAuthStore((state) => state.token);
  const isHydrating = useAuthStore((state) => state.isHydrating);

  // Redirect to login if not authenticated (but wait for hydration to complete)
  useEffect(() => {
    if (!isHydrating && !user) {
      navigate('/login');
    }
  }, [isHydrating, user, navigate]);

  const [loading, setLoading] = useState(false);
  const [countryCode, setCountryCode] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('bog'); // 'bog' or 'test'
  const [exchangeRates, setExchangeRates] = useState(null);
  const [currency] = useState(() => {
    // Initialize currency based on user's registration country
    if (user?.registration_country) {
      return getCurrencyFromCountry(user.registration_country);
    }
    return 'USD'; // Default to USD
  });
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: user?.email || '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
  });

  // Fetch exchange rates on component mount
  useEffect(() => {
    const loadExchangeRates = async () => {
      const rates = await fetchExchangeRates();
      setExchangeRates(rates);
    };
    loadExchangeRates();
  }, []);

  // Fetch user's addresses on component mount
  useEffect(() => {
    const fetchAddresses = async () => {
      if (!token) return;
      try {
        const response = await fetch(`${API_BASE_URL}/api/addresses`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          setAddresses(data);
          // Auto-select first address if available
          if (data.length > 0) {
            setSelectedAddressId(data[0].id);
            fillFormWithAddress(data[0]);
          }
        }
      } catch (error) {
        console.error('Error fetching addresses:', error);
      }
    };
    fetchAddresses();
  }, [token]);

  // Detect country on component mount
  useEffect(() => {
    const detectCountry = async () => {
      const code = await getCountryFromIP();
      setCountryCode(code);
      if (code && !selectedAddressId) {
        // Auto-fill country field only if no address is selected
        setFormData((prev) => ({ ...prev, country: code }));
      }
    };
    detectCountry();
  }, [selectedAddressId]);

  // Get current price for a cart item (from products store)
  const getCurrentPrice = (cartItem) => {
    const product = products.find(p => p.id === cartItem.id);
    // Use current product price if available, otherwise use stored price
    return product ? parseFloat(product.price) : parseFloat(cartItem.price);
  };

  // Get current discount price for a cart item
  const getCurrentDiscountPrice = (cartItem) => {
    const product = products.find(p => p.id === cartItem.id);
    return product?.discount_price ? parseFloat(product.discount_price) : 0;
  };

  // Calculate subtotal using current prices
  const subtotal = cart.reduce((sum, item) => {
    const currentPrice = getCurrentPrice(item);
    const discountPrice = getCurrentDiscountPrice(item);
    const displayPrice = discountPrice > 0 ? discountPrice : currentPrice;
    return sum + displayPrice * item.quantity;
  }, 0);

  const shippingInfo = getShippingCost(countryCode, subtotal);
  const shipping = shippingInfo.cost;
  const total = subtotal + shipping;

  const fillFormWithAddress = (address) => {
    setFormData((prev) => ({
      ...prev,
      address: address.street_address,
      city: address.city,
      state: address.postal_code?.split(' ')[0] || '',
      zipCode: address.postal_code,
      country: address.country,
    }));
    setCountryCode(address.country);
  };

  const handleAddressSelect = (e) => {
    const addressId = parseInt(e.target.value);
    if (addressId === -1) {
      // New address option selected
      setShowNewAddressForm(true);
      setSelectedAddressId(null);
    } else {
      setShowNewAddressForm(false);
      const selectedAddr = addresses.find(a => a.id === addressId);
      if (selectedAddr) {
        setSelectedAddressId(addressId);
        fillFormWithAddress(selectedAddr);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Detect country from postal code when it changes
    if (name === 'zipCode' && value) {
      const detectedCountry = getCountryFromPostalCode(value);
      if (detectedCountry) {
        setCountryCode(detectedCountry);
      }
    }
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate form data - only require name, phone, and address (auto-filled from selected address)
      if (!formData.firstName || !formData.lastName || !formData.phone || !formData.address) {
        alert('Please fill in all required fields and select/add a delivery address.');
        setLoading(false);
        return;
      }

      // Handle test payment
      if (paymentMethod === 'test') {
        try {
          // Build shipping address from available fields
          const addressParts = [
            formData.address,
            formData.city,
            formData.state,
            formData.zipCode,
            formData.country
          ].filter(Boolean); // Remove empty/undefined values

          const shippingAddress = addressParts.join(', ');

          // Create order directly for testing (without going through BoG payment)
          const orderResponse = await fetch(`${API_BASE_URL}/api/orders/create`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              items: cart,
              shipping_address: shippingAddress,
              subtotal: parseFloat(subtotal),
              tax: 0,
              shipping: parseFloat(shipping),
              total: parseFloat(total),
              notes: 'TEST PAYMENT - Order placed through test mode'
            }),
          });

          const orderData = await orderResponse.json();

          if (orderResponse.ok && orderData.order_number) {
            // Store checkout data in sessionStorage for PaymentSuccess to retrieve
            sessionStorage.setItem('checkoutData', JSON.stringify({
              formData,
              subtotal,
              shipping,
              total,
              shippingCurrency: shippingInfo.currency,
              orderNumber: orderData.order_number,
              isTestPayment: true
            }));
            // Redirect to payment success page
            navigate('/payment-success', { state: { testPayment: true, orderNumber: orderData.order_number } });
          } else {
            alert(`Failed to create test order: ${orderData.error || 'Unknown error'}`);
            setLoading(false);
          }
        } catch (error) {
          console.error('Test payment error:', error);
          alert('Test payment failed. Please try again.');
          setLoading(false);
        }
        return;
      }

      // Call backend for Bank of Georgia payment
      // Convert total to GEL since BoG only accepts GEL
      const totalInGEL = convertToGEL(total, currency, exchangeRates);

      console.log(`Converting ${formatPrice(total, currency)} to GEL: ${formatPrice(totalInGEL, 'GEL')}`);

      const response = await fetch(`${API_BASE_URL}/api/create-bog-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          items: cart,
          total: totalInGEL, // Send amount in GEL
          customer: formData,
          currency: 'GEL', // Always GEL for BoG
          displayCurrency: currency, // User's preferred display currency
        }),
      });

      const data = await response.json();

      console.log('Payment response:', data, 'Status:', response.status);

      if (!response.ok) {
        console.error('Payment error:', data);
        const errorMsg = data.error || `Payment failed (${response.status}). Please try again.`;
        alert(errorMsg);
        setLoading(false);
        return;
      }

      if (data.paymentUrl) {
        console.log('Redirecting to payment URL:', data.paymentUrl);
        // Store checkout data in sessionStorage for PaymentSuccess to retrieve
        sessionStorage.setItem('checkoutData', JSON.stringify({
          formData,
          subtotal,
          shipping,
          total,
          shippingCurrency: shippingInfo.currency,
        }));
        window.location.href = data.paymentUrl;
      } else {
        console.error('No payment URL returned:', data);
        alert('Payment processing failed. Please try again.');
        setLoading(false);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Payment failed. Please try again.');
      setLoading(false);
    }
  };

  if (!user) {
    return null; // Will redirect via useEffect
  }

  if (cart.length === 0) {
    return (
      <div className="checkout-page">
        <div className="checkout-container">
          <p>Your cart is empty. Please add items before checking out.</p>
          <button onClick={() => navigate('/products')} className="btn-primary">
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <div className="checkout-container">
        <h1>Checkout</h1>

        <div className="checkout-content">
          {/* Checkout Form */}
          <div className="checkout-form">
            <form onSubmit={handleCheckout}>
              {/* Customer Info */}
              <section className="form-section">
                <h2>Delivery Information</h2>

                {/* Address Selection Dropdown */}
                <div className="form-group">
                  <label>Select Delivery Address *</label>
                  <select
                    value={selectedAddressId || ''}
                    onChange={handleAddressSelect}
                    required={!showNewAddressForm}
                  >
                    <option value="">Choose an address...</option>
                    {addresses.map((addr) => (
                      <option key={addr.id} value={addr.id}>
                        {addr.street_address}, {addr.city}, {addr.country}
                      </option>
                    ))}
                    <option value="-1">+ Add New Address</option>
                  </select>
                </div>

                {/* Show address details when selected (read-only) */}
                {selectedAddressId && !showNewAddressForm && (
                  <div className="address-preview">
                    <p><strong>{formData.address}</strong></p>
                    <p>{formData.city}, {formData.state} {formData.zipCode}</p>
                    <p>{formData.country}</p>
                  </div>
                )}

                {/* New Address Form */}
                {showNewAddressForm && (
                  <div className="new-address-form">
                    <h3>Add New Address</h3>
                    <div className="form-group">
                      <label>Street Address *</label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="form-group-row">
                      <div className="form-group">
                        <label>City *</label>
                        <input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>State/Province *</label>
                        <input
                          type="text"
                          name="state"
                          value={formData.state}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="form-group-row">
                      <div className="form-group">
                        <label>ZIP Code *</label>
                        <input
                          type="text"
                          name="zipCode"
                          value={formData.zipCode}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Country *</label>
                        <input
                          type="text"
                          name="country"
                          value={formData.country}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Recipient Information */}
                <div className="form-group-row">
                  <div className="form-group">
                    <label>Recipient First Name *</label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Recipient Last Name *</label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Recipient Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </section>

              {/* Payment Method */}
              <section className="form-section">
                <h2>Payment Method</h2>

                {/* Payment Currency Notice */}
                <div style={{
                  padding: '1rem',
                  backgroundColor: '#f0f7ff',
                  border: '1px solid #90caf9',
                  borderRadius: '4px',
                  marginBottom: '1.5rem'
                }}>
                  <p style={{ margin: 0, fontSize: '0.9rem', color: '#1565c0', marginBottom: '0.5rem' }}>
                    <strong>Payment Currency:</strong> All payments are processed in Georgian Lari (GEL).
                    Prices shown in your preferred currency ({currency}) will be automatically converted to GEL for payment.
                  </p>
                  {exchangeRates && currency !== 'GEL' && (
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#1976d2' }}>
                      Current rate: 1 {currency} = {exchangeRates[currency]?.toFixed(4)} GEL
                      {' • '}
                      Your total: {formatPrice(total, currency)} ≈ {formatPrice(convertToGEL(total, currency, exchangeRates), 'GEL')}
                    </p>
                  )}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer', padding: '1rem', border: `2px solid ${paymentMethod === 'bog' ? '#de4010' : '#ddd'}`, borderRadius: '4px', backgroundColor: paymentMethod === 'bog' ? '#fff5f0' : '#fff' }}>
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="bog"
                      checked={paymentMethod === 'bog'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      style={{ marginTop: '0.25rem', cursor: 'pointer' }}
                    />
                    <div>
                      <p style={{ margin: 0, fontWeight: '600', marginBottom: '0.5rem' }}>Bank of Georgia</p>
                      <p style={{ color: '#666', fontSize: '0.9rem', margin: 0 }}>
                        Secure payment processing via Bank of Georgia. You will be redirected to complete the transaction.
                      </p>
                    </div>
                  </label>

                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer', padding: '1rem', border: `2px solid ${paymentMethod === 'test' ? '#de4010' : '#ddd'}`, borderRadius: '4px', backgroundColor: paymentMethod === 'test' ? '#fff5f0' : '#fff' }}>
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="test"
                      checked={paymentMethod === 'test'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      style={{ marginTop: '0.25rem', cursor: 'pointer' }}
                    />
                    <div>
                      <p style={{ margin: 0, fontWeight: '600', marginBottom: '0.5rem' }}>Test Payment (Development)</p>
                      <p style={{ color: '#666', fontSize: '0.9rem', margin: 0 }}>
                        Skip payment processing for testing purposes. Order will be created immediately without payment verification.
                      </p>
                    </div>
                  </label>
                </div>
              </section>

              <button type="submit" className="btn-pay" disabled={loading}>
                {loading ? 'Processing...' : paymentMethod === 'test' ? `Test Payment - ${formatPrice(total, currency)}` : `Pay ${formatPrice(total, currency)}`}
              </button>
            </form>
          </div>

          {/* Order Summary */}
          <div className="checkout-summary">
            <h2>Order Summary</h2>
            <div className="summary-items">
              {cart.map((item) => {
                const currentPrice = getCurrentPrice(item);
                const discountPrice = getCurrentDiscountPrice(item);
                const displayPrice = discountPrice > 0 ? discountPrice : currentPrice;

                return (
                  <div key={item.cartId} className="summary-item">
                    <span className="item-info">
                      <strong>{item.name}</strong>
                      <div className="item-specs">
                        {item.size && <span className="spec">Size: {item.size}</span>}
                        {item.color && <span className="spec">Color: {item.color}</span>}
                      </div>
                      <span className="item-qty">x{item.quantity}</span>
                    </span>
                    <span className="item-price">
                      {formatPrice(displayPrice * item.quantity, currency)}
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="summary-totals">
              <div className="summary-row">
                <span>Subtotal:</span>
                <span>{formatPrice(subtotal, currency)}</span>
              </div>
              <div className="summary-row">
                <span>Shipping:</span>
                <span>
                  {shipping === 0 ? (
                    <span className="free">Free</span>
                  ) : (
                    formatPrice(shipping, currency)
                  )}
                </span>
              </div>
              <p className="shipping-info">{shippingInfo.description}</p>
              <div className="summary-row total">
                <span>Total:</span>
                <span>{formatPrice(total, currency)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
